import React from 'react';
import moment from 'moment';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import classnames from 'classnames/bind';
import { ResponsivePNG } from 'sports-react';

import StyledComponent from '~/components/Styled/Styled';
import ScrollMenu from '~/components/ScrollMenu/ScrollMenu';
import LoadingSpinner from '~/components/LoadingSpinner/LoadingSpinner';
import ScrollMenuElement from '~/components/ScrollMenuElement/ScrollMenuElement';

import ResultsMap from '~/constants/ResultsMap';

import getOr from '~/util/getOr';

import css from './TennisResultsMatchupCard.scss';

const cssContext = classnames.bind(css);

type Props = {
  event: any;
  eventRecords: any;
};

type State = {
  navElementSelected: any;
  eventId: any;
};

class TennisResultsMatchupCard extends React.Component<Props, State> {
  static displayName = 'TennisResultsMatchupCard';

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.event.id === prevState.eventId) {
      return null;
    }

    const leagueSlug = getOr(nextProps, 'event.league.slug', '');
    const eventStatus = getOr(nextProps, 'event.event_status', '');
    const schedule = getOr(
      nextProps,
      `eventRecords.[${leagueSlug}/${nextProps.event.id}/schedule].event`,
      []
    );
    const navElementSelected =
      eventStatus === 'pre_game' ? schedule[0] : schedule[schedule.length - 1];
    return {
      navElementSelected,
      eventId: nextProps.event.id,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      navElementSelected: null,
      eventId: null,
    };
  }

  getOdds(teamId, event) {
    const isHomeTeam = teamId === event.home_team_id;
    if (isHomeTeam) {
      return getOr(event, 'odds.home', null);
    }
    return getOr(event, 'odds.away', null);
  }

  formatDescriptions(descriptionElement, event) {
    const propertyKeys = getOr(descriptionElement, 'properties', []);
    const format = getOr(descriptionElement, 'format', null);
    const seperator = getOr(descriptionElement, 'seperator', '');
    const label = getOr(descriptionElement, 'label', '');
    const currentEventStatus = getOr(this.props, 'event.event_status', '');
    const eventStatusAllowed = getOr(descriptionElement, 'event_status', []);
    let properties = propertyKeys.map(propertyKey => getOr(event, propertyKey, null));
    if (eventStatusAllowed.length && !eventStatusAllowed.includes(currentEventStatus)) {
      return null;
    }
    switch (descriptionElement.type) {
      case 'date':
        properties = properties.map(property => moment(property).format(format));
        break;
      case 'text':
        if (format) {
          let description = format;
          properties.forEach((property, i) => {
            description = description.replace(`%[${i}]`, property);
          });
          return `${label}${label && ': '}${description}`;
        }
        break;
      default:
        break;
    }
    return `${label}${label && ': '}${properties.join(seperator)}`;
  }

  menuElementClicked(navElementSelected) {
    this.setState({ navElementSelected });
  }

  renderMenuElements(eventNavElements) {
    return (
      <div className={css.scrollMenuContainer}>
        <ScrollMenu buttons>
          {eventNavElements.map(navElement => (
            <div key={navElement.label}>
              <ScrollMenuElement
                name={navElement.label}
                selected={navElement.id === getOr(this.state.navElementSelected, 'id', null)}
                onClick={() => {
                  this.menuElementClicked(navElement);
                }}
              />
            </div>
          ))}
        </ScrollMenu>
      </div>
    );
  }

  renderEventCard(eventCardDescription, event) {
    const eventStatus = getOr(event, 'event_status', '');
    const numberOfSets = getOr(event, 'sets', 0);
    const team1 = getOr(event, 'team_1', null);
    const team2 = getOr(event, 'team_2', null);
    const winner = getOr(event, 'winner', {});
    const team1Won = team1.player1.id === getOr(winner, 'player1.id', null);
    const team2Won = team2.player1.id === getOr(winner, 'player1.id', null);
    const team1Seed = getOr(event, 'team_1_seed', null);
    const team2Seed = getOr(event, 'team_2_seed', null);
    const team1Scores = getOr(event, 'team_1_scores', []);
    const team2Scores = getOr(event, 'team_2_scores', []);
    const team1TieBreaks = getOr(event, 'team_1_tie_break', []);
    const team2TieBreaks = getOr(event, 'team_2_tie_break', []);
    const team1NameClasses = cssContext(css.playerName, {
      playerLost: team2Won,
    });
    const team2NameClasses = cssContext(css.playerName, {
      playerLost: team1Won,
    });
    const team1Odds = this.getOdds(team1.id, event);
    const team2Odds = this.getOdds(team2.id, event);

    const eventDescription = eventCardDescription[eventStatus] || eventCardDescription.default;

    return (
      <div className={css.eventCardContainer} key={event.id}>
        <div className={css.eventCardDescription}>
          {this.formatDescriptions(eventDescription, event)}
        </div>
        <div className={css.scoresContainer}>
          {Array.from(Array(numberOfSets).keys()).map(set => (
            <div key={set} className={`${css.setHeaderCell}`}>
              {set + 1}
            </div>
          ))}
        </div>
        <div className={css.playerContainer}>
          <div className={css.playerFlag}>
            <ResponsivePNG
              width="24px"
              height="24px"
              src={getOr(team1, 'player1.flag.tiny', null)}
            />
          </div>
          <div className={team1NameClasses}>
            {!!team1Seed && `(${team1Seed}) `}
            {getOr(team1, 'player1.first_name[0]', '')}
            .&nbsp;
            {getOr(team1, 'player1.last_name', '')}
          </div>
          <div className={css.scoresContainer}>
            {eventStatus !== 'pre_game' && team1Scores.length > 0 ? (
              team1Scores.map((score, i) => {
                const team1TieBreak = getOr(team1TieBreaks, `[${i}]`, 0);
                const team2TieBreak = getOr(team2TieBreaks, `[${i}]`, 0);
                const teamScoreClasses = cssContext(css.scoreContainer, {
                  playerLost: score + team1TieBreak < team2Scores[i] + team2TieBreak,
                });
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <div className={teamScoreClasses} key={`team1Score${i}`}>
                    <div className={css.scoreCell}>{score}</div>
                    <div className={css.tieBreak}>{!!team1TieBreak && team1TieBreak}</div>
                  </div>
                );
              })
            ) : (
              <div className={css.oddsDisplay}>{team1Odds}</div>
            )}
          </div>
        </div>
        <div className={css.playerContainer}>
          <div className={css.playerFlag}>
            <ResponsivePNG
              width="24px"
              height="24px"
              src={getOr(team2, 'player1.flag.tiny', null)}
            />
          </div>
          <div className={team2NameClasses}>
            {!!team2Seed && `(${team2Seed}) `}
            {getOr(team2, 'player1.first_name[0]', '')}
            .&nbsp;
            {getOr(team2, 'player1.last_name', '')}
          </div>
          <div className={css.scoresContainer}>
            {eventStatus !== 'pre_game' && team2Scores.length > 0 ? (
              team2Scores.map((score, i) => {
                const team1TieBreak = getOr(team1TieBreaks, `[${i}]`, 0);
                const team2TieBreak = getOr(team2TieBreaks, `[${i}]`, 0);
                const teamScoreClasses = cssContext(css.scoreContainer, {
                  playerLost: score + team2TieBreak < team1Scores[i] + team1TieBreak,
                });
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <div className={teamScoreClasses} key={`team2Score${i}`}>
                    <div className={css.scoreCell}>{score}</div>
                    <div className={css.tieBreak}>{!!team2TieBreak && team2TieBreak}</div>
                  </div>
                );
              })
            ) : (
              <div className={css.oddsDisplay}>{team2Odds}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const leagueSlug = getOr(this.props, 'event.league.slug', '');
    const eventRecords = getOr(this.props, 'eventRecords', {});
    const schedule = getOr(
      eventRecords,
      `[${leagueSlug}/${this.state.eventId}/schedule].event`,
      []
    );

    let events = getOr(eventRecords, `[${leagueSlug}/${this.state.eventId}/matches]`, []);
    events = orderBy(groupBy(events, 'round_name'), 'length', 'desc');

    // eslint-disable-next-line no-prototype-builtins
    if (!eventRecords.hasOwnProperty(`${leagueSlug}/${this.state.eventId}/matches`)) {
      return (
        <div className={css.pregameNoticeContainer}>
          <div className={css.fetchingDataSpinner}>
            <LoadingSpinner SpinnerType="square_flip" />
          </div>
          <div className={css.preGameNoticeText1}>Please wait</div>
          <div className={css.preGameNoticeText2}>Fetching your data</div>
        </div>
      );
    }
    if (!events.length) {
      return (
        <div className={css.pregameNoticeContainer}>
          <div className={css.preGameNoticeText1}>Event hasn&apos;t started yet</div>
          <div className={css.preGameNoticeText2}>Please check back later for more details</div>
        </div>
      );
    }
    const eventCardDescription = getOr(
      ResultsMap,
      `${leagueSlug}.matchupCards.eventCardDescriptions`,
      {}
    );
    const selectedEvents = events.map(round => {
      const eventStatusOrder = ['in_progress', 'pre_game', 'final'];
      return eventStatusOrder
        .map(status =>
          round.filter(event => {
            const matchIds = getOr(this.state, 'navElementSelected.match_ids', []);
            return event.event_status === status && matchIds.includes(event.id);
          })
        )
        .reduce((acc, val) => acc.concat(val), []);
    });
    return (
      <div className={css.matchupRecordsContainer}>
        {this.renderMenuElements(schedule)}
        {selectedEvents.map(round => {
          if (!round.length) {
            return null;
          }
          const roundName = getOr(round, '[0].round_name', '');
          return (
            <div key={roundName}>
              <div className={css.eventCardTitle}>{roundName}</div>
              {round.map(event => this.renderEventCard(eventCardDescription, event))}
            </div>
          );
        })}
      </div>
    );
  }
}

export default StyledComponent(TennisResultsMatchupCard, [css]);
